import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { PostSiteType, SiteType, SubsiteType } from "../../../../types/welcome";
import { getStudySites } from "../../../../services/study.service";
import ResonanceLoader from "../../../atoms/ResonanceLoader/ResonanceLoader";
import DisplaySitesList from "../../../molecules/Home/DisplaySitesList";
import Container from "../../../atoms/Container";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import { postSite } from "../../../../services/user.service";
import { useFetch } from "../../../../hooks/useFetch";
import Info from "../../../atoms/ErrorComponent";
import useToast from "../../../../hooks/useToast";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { useConfig } from "../../../../contexts/ConfigContext";
import useVocalisation from "../../../../hooks/useVocalisation";
import styled from "styled-components";
import { volumeHighOutline } from "ionicons/icons";
import { IonIcon } from "@ionic/react";
const DisplaySites = (): ReactElement => {
  const { welcomeData, goPrevious, previousStep, completeSiteStep } = useHomeContext();
  const studyId = welcomeData.id;
  const { selectedRole } = useConfig();
  const { readText } = useVocalisation();
  const isVocalisationEnabled = selectedRole?.attributes?.is_vocalisation_enabled;
  const { t } = useTranslation();
  const [selectedSite, setSelectedSite] = useState<SiteType | null>(null);
  const { isError, isLoading, data: sites } = useFetch<SiteType[]>("sites", getStudySites, { studyId });
  const present = useToast();
  const onSiteSelect = async (siteId: number, subSiteId?: number) => {
    try {
      const body: PostSiteType = { sites: [{ site_id: siteId, subsites: subSiteId ? [subSiteId] : [] }] };
      await postSite(studyId, body);
      completeSiteStep();
    } catch (e) { console.log(e); present(e?.response?.data?.message); }
  };
  const handleSiteClick = (site: SiteType): void => { if (site.subsites.length) { setSelectedSite(site); } else { onSiteSelect(site.id); } };
  const handleSubsiteClick = ({ id }: SubsiteType): void => { onSiteSelect(selectedSite.id, id); };
  const handlePreviousClick = (): void => { if (selectedSite) { setSelectedSite(null); } else if (previousStep !== "INSCRIPTION") { goPrevious(); } };
  const labelKey = selectedSite ? "subsite" : "site";
  if (isLoading) return <ResonanceLoader />;
  if (isError) return (<Info title={t("common_server_error_title")} description={t("common_server_error_desc")} actionText={t("common_go_back")} />);
  const headerTitle = selectedRole?.site[`question_${labelKey}`] !== '' && selectedRole?.site[`question_${labelKey}`] ? selectedRole?.site[`question_${labelKey}`] : t(`home_${labelKey}s_text`);
  return (
    <Container>
      <PageHeader>
        <PageTitle>{headerTitle}</PageTitle>
        {isVocalisationEnabled && (<SpeakButton type="button" aria-label="read title" onClick={() => readText(headerTitle)}><SpeakIcon icon={volumeHighOutline} /></SpeakButton>)}
      </PageHeader>
      {selectedSite ? (<DisplaySitesList sites={selectedSite.subsites} onSiteClick={handleSubsiteClick} />) : (<DisplaySitesList sites={sites} onSiteClick={handleSiteClick} />)}
      <NextPrevButtons onPreviousClick={selectedSite && handlePreviousClick} />
    </Container>
  );
};
const PageHeader = styled.header`display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 28px 20px 12px;`;
const PageTitle = styled.h1`flex: 1; margin: 0; text-align: start; color: #393939; font-weight: 700; font-size: 26px; line-height: 1.25; overflow-wrap: anywhere;
  /* Hard-codes its own size, so the global desktop heading step in App.scss
     cannot reach it (class beats element selector) — stepped down here. */
  @media (min-width: 768px) { font-size: 20px; }`;
const SpeakButton = styled.button`flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; margin-top: 2px; border: none; border-radius: 50%; background: transparent; cursor: pointer; color: ${({ theme }) => theme.primary.main}; &:hover, &:active { background-color: rgba(13,140,155,0.12); }`;
const SpeakIcon = styled(IonIcon)`font-size: 22px;`;
export default DisplaySites;
